<template>
  <div class="promotions-cards">
    <div
      v-if="loading"
      class="p-6 text-center"
    >
      <i class="fas fa-spinner fa-spin text-2xl text-gray-400 dark:text-gray-500" />
      <p class="mt-2 text-gray-600 dark:text-gray-400">
        Cargando promociones...
      </p>
    </div>

    <div
      v-else-if="!promotions.length"
      class="p-6 text-center"
    >
      <i class="fas fa-tag mb-4 text-4xl text-gray-300 dark:text-gray-600" />
      <p class="text-gray-600 dark:text-gray-400">
        No hay promociones configuradas
      </p>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-500">
        Crea tu primera promoción para comenzar a ofrecer descuentos
      </p>
    </div>

    <div
      v-else
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
    >
      <div
        v-for="promotion in promotions"
        :key="promotion.id"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
      >
        <!-- Card Header -->
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {{ promotion.name }}
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {{ promotion.description }}
              </p>
            </div>
            <div class="flex items-center space-x-2 ml-3 flex-shrink-0">
              <span
                :class="getStatusBadgeClass(promotion.is_active)"
              >
                {{ getStatusLabel(promotion.is_active) }}
              </span>
              <DropdownMenu
                :id="`promotion-card-menu-${promotion.id}`"
                button-label="Acciones"
                width="sm"
              >
                <template
                  v-for="action in getPromotionActions(promotion)"
                  :key="action.key"
                >
                  <DropdownMenuDivider v-if="action.dividerBefore" />
                  <DropdownMenuItem
                    :icon="action.icon"
                    :variant="action.variant"
                    @click="action.onClick"
                  >
                    {{ action.label }}
                  </DropdownMenuItem>
                </template>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <!-- Card Body -->
        <div class="p-4 space-y-3">
          <!-- Type -->
          <div class="flex items-center">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-20">Tipo:</span>
            <span class="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {{ getPromotionTypeLabel(promotion.action?.type) || 'Sin tipo' }}
            </span>
          </div>

          <!-- Time Condition -->
          <div class="flex items-start">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-20">Condición:</span>
            <div class="flex-1">
              <div v-if="promotion.time_condition">
                <div class="flex flex-wrap gap-1 mb-1">
                  <span
                    v-for="day in promotion.time_condition.days_of_week"
                    :key="day"
                    class="inline-flex rounded bg-gray-100 px-2 py-1 text-xs dark:bg-gray-700"
                  >
                    {{ getDayLabel(day) }}
                  </span>
                </div>
                <div
                  v-if="promotion.time_condition.start_time && promotion.time_condition.end_time"
                  class="text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ promotion.time_condition.start_time }} - {{ promotion.time_condition.end_time }}
                </div>
              </div>
              <div
                v-else
                class="text-sm text-gray-400"
              >
                Siempre activa
              </div>
            </div>
          </div>

          <!-- Usage -->
          <div class="flex items-center">
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 w-20">Usos:</span>
            <span class="text-sm text-gray-900 dark:text-white">
              {{ promotion.used_count || 0 }}
              <span
                v-if="promotion.max_uses_total"
                class="text-gray-500"
              >/ {{ promotion.max_uses_total }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PauseIcon, PencilIcon, PlayIcon, TrashIcon } from '@heroicons/vue/24/outline';
import type { Component } from 'vue';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import type { Promotion, PromotionType, WeekDay } from '@/types/promotion';

defineProps<{
  promotions: Promotion[];
  loading: boolean;
}>();

const emit = defineEmits<{
  edit: [promotion: Promotion];
  toggle: [promotion: Promotion];
  delete: [promotion: Promotion];
}>();

type ActionVariant = 'default' | 'warning' | 'success' | 'danger';

interface PromotionMenuAction {
  key: string;
  label: string;
  icon: Component;
  variant: ActionVariant;
  onClick: () => void;
  dividerBefore?: boolean;
}

const promotionTypeLabels: Record<PromotionType, string> = {
  buy_x_get_y: 'Compra X obtén Y',
  fixed_price: 'Precio Fijo',
  percentage_discount: 'Descuento %',
  fixed_amount_discount: 'Descuento Fijo',
  free_item: 'Item Gratis',
  bundle_price: 'Paquete Especial',
  buy_x_for_y_price: 'Compra X por Y precio'
};

const dayLabels: Record<WeekDay, string> = {
  monday: 'Lun',
  tuesday: 'Mar',
  wednesday: 'Mié',
  thursday: 'Jue',
  friday: 'Vie',
  saturday: 'Sáb',
  sunday: 'Dom'
};

function getStatusBadgeClass(isActive: boolean): string {
  return isActive
    ? 'inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : 'inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
}

function getStatusLabel(isActive: boolean): string {
  return isActive ? 'Activa' : 'Inactiva';
}

function getPromotionTypeLabel(type?: string): string {
  if (!type) {
    return 'Sin tipo';
  }

  return promotionTypeLabels[type as PromotionType] || type;
}

function getDayLabel(day: string): string {
  return dayLabels[day as WeekDay] || day;
}

function getPromotionActions(promotion: Promotion): PromotionMenuAction[] {
  return [
    {
      key: 'edit',
      label: 'Editar',
      icon: PencilIcon,
      variant: 'default',
      onClick: () => emit('edit', promotion)
    },
    {
      key: 'toggle',
      label: promotion.is_active ? 'Desactivar' : 'Activar',
      icon: promotion.is_active ? PauseIcon : PlayIcon,
      variant: promotion.is_active ? 'warning' : 'success',
      onClick: () => emit('toggle', promotion)
    },
    {
      key: 'delete',
      label: 'Eliminar',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => emit('delete', promotion),
      dividerBefore: true
    }
  ];
}
</script>
